import React, { useState, useEffect, useContext } from "react";
import Text from "antd/lib/typography/Text";
import { Row, Col, Skeleton, Avatar, Spin, Table, Select, Button } from "antd";
import { Pie, Area, TinyLine } from "@ant-design/charts";
import { useHistory } from "react-router-dom";
import {
  ShoppingOutlined,
  // PoundOutlined, // Remove this import
  UnorderedListOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  BarChartOutlined,
  AppstoreOutlined,
  MobileOutlined,
} from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faIndianRupeeSign } from "@fortawesome/free-solid-svg-icons";

import "./index.css";
import { DateFormat, getApi, preloadImage } from "../../helpers";
import CardContainer from "../../components/Card";
import { StoreContext } from "../../context";
import fallbackProductImage from "../../assets/bucket_img.png";

var tinyLineConfig = {
  height: 50,
  color: "#17CC73",
  smooth: true,
  // areaStyle: { fill: "#17CC73" },
};

let pieConfig = {
  autoFit: true,
  angleField: "value",
  colorField: "type",
  color: ({ type }) => {
    if (type === "in_store") {
      return "#1f1f1f";
    }
    return "#4F7CFF";
  },
  legend: false,
  height: 250,
  width: 250,
  radius: 0.8,
  innerRadius: 0.7,
  label: false,
  interactions: [{ type: "element-active" }],
  // Add this to ensure chart renders even with 0 values
  meta: {
    value: {
      min: 0,
    },
  },
};

var areaConfig = {
  xField: "time",
  yField: "orders",
  xAxis: {
    range: [0, 1],
    tickCount: 12,
    label: {
      formatter: (text) => {
        const hour = parseInt(text);
        return `${hour}:00`;
      },
    },
  },
  yAxis: {
    label: {
      formatter: (text) => `${text}`,
    },
  },
  height: 200,
  color: "#4F7CFF",
  smooth: true,
  areaStyle: function areaStyle() {
    return {
      fill: "l(270) 0:#ffffff 0.3:#4F7CFF40 1:#4F7CFF80",
      fillOpacity: 0.6,
    };
  },
  line: {
    color: "#4F7CFF",
    size: 2,
  },
};

// Custom Dropdown Component
const CustomDropdown = ({ value, onChange, options, style }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = React.useRef(null);
  const triggerRef = React.useRef(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const getDisplayText = () => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : value;
  };

  return (
    <div className="custom-dropdown" style={style} ref={dropdownRef}>
      <div
        ref={triggerRef}
        className="custom-dropdown-trigger"
        onClick={handleToggle}
      >
        <span>{getDisplayText()}</span>
        <span className={`custom-dropdown-arrow ${isOpen ? "open" : ""}`}>
          ▼
        </span>
      </div>
      {isOpen && (
        <div
          className="custom-dropdown-menu"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width,
          }}
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={`custom-dropdown-item ${
                value === option.value ? "selected" : ""
              }`}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Custom wrapper to match Ant Design icon style
const RupeeIcon = () => (
  <span
    className="anticon"
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "24px",
      lineHeight: 1,
    }}
  >
    <FontAwesomeIcon
      icon={faIndianRupeeSign}
      style={{
        fontSize: "24px",
        verticalAlign: "middle",
        color: "#4F7CFF",
      }}
    />
  </span>
);

export default function Dashboard() {
  const { storeId } = useContext(StoreContext);
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [failedImages, setFailedImages] = useState(new Set());
  const [orderChartFilter, setOrderChartFilter] = useState("weekly");
  const [topSKUsFilter, setTopSKUsFilter] = useState("hourly");
  const [data, setData] = useState({
    topProduct: {},
    hourly_chart: [],
    piechart: [],
    recentOrders: [],
    topSKUs: [],
  });

  function getDateRangeForFilter(filter) {
    const today = new Date();
    const fmt = (d) => d.toISOString().split("T")[0];
    if (filter === "hourly") {
      return { start_date: fmt(today), end_date: fmt(today) };
    } else if (filter === "weekly") {
      const start = new Date(today);
      start.setDate(today.getDate() - 6);
      return { start_date: fmt(start), end_date: fmt(today) };
    } else {
      const start = new Date(today);
      start.setDate(today.getDate() - 29);
      return { start_date: fmt(start), end_date: fmt(today) };
    }
  }

  const filterOptions = [
    { value: "hourly", label: "Hourly" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
  ];

  const handleCardClick = (route) => {
    history.push(route);
  };

  const getAvatarColor = (name) => {
    const colors = [
      "#f56a00",
      "#7265e6",
      "#ffbf00",
      "#00a2ae",
      "#87d068",
      "#108ee9",
    ];
    const index = name ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
  };

  const getStatFontSize = (value) => {
    const len = String(value).length;
    if (len <= 4) return 20;
    if (len <= 6) return 17;
    if (len <= 8) return 15;
    return 13;
  };

  useEffect(() => getData(), [storeId, orderChartFilter]);
  useEffect(() => getOrderChartData(), [orderChartFilter, storeId]);
  useEffect(() => getTopSKUsData(), [orderChartFilter, storeId]);

  async function getData() {
    setLoading(true);
    let payload = { filter: orderChartFilter };
    if (storeId) payload.store_id = storeId;

    try {
      const { start_date, end_date } = getDateRangeForFilter(orderChartFilter);
      const ordersPayload = { start_date, end_date, limit: 10, page: 0 };
      if (storeId) ordersPayload.store_id = storeId;

      const [res1, res3] = await Promise.all([
        getApi(payload, "dashboardCounts", "dashboard/"),
        // getApi(payload, "totalUsers", "dashboard/"),
        getApi(ordersPayload, "getOrders"),
      ]);

      if (res1.data && res3.data) {
        setData((prevData) => ({
          ...prevData,
          counts: res1.data,
          recentOrders: res3.data,
          topProduct: res1.data.topProduct || {},
        }));

        if (res1.data?.topProduct?.image?.url) {
          preloadImage(res1.data?.topProduct?.image?.url);
        }
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  }

  async function getOrderChartData() {
    let payload = { type: orderChartFilter };
    if (storeId) payload.store_id = storeId;

    try {
      const res = await getApi(payload, "ordersChart", "dashboard/");
      if (res?.data) {
        console.log("Piechart data:", res.data.piechart);
        setData((prevData) => ({
          ...prevData,
          hourly_chart: res.data.linegraph || res.data.hourly_chart || [],
          piechart: res.data.piechart || [],
        }));
      }
    } catch (error) {
      console.error("Error fetching order chart data:", error);
    }
  }

  async function getTopSKUsData() {
    let payload = { filter: orderChartFilter };
    if (storeId) payload.store_id = storeId;

    try {
      const res = await getApi(payload, "topProducts", "dashboard/");
      if (res?.data) {
        setData((prevData) => ({
          ...prevData,
          topSKUs: res.data || [],
        }));
      }
    } catch (error) {
      console.error("Error fetching top SKUs data:", error);
    }
  }

const topSKUsColumns = [
  {
    title: "Image",
    dataIndex: "image_url",
    key: "image_url",
    width: 60,
    render: (imageUrl, record) => {
      // Use the image_url directly from the API response
      const finalImageUrl = imageUrl || record.image_url;

      // Check if URL is likely to be problematic (AWS access denied patterns)
      const isProblematicUrl =
        finalImageUrl &&
        (finalImageUrl.includes("s3.ap-south-1.amazonaws.com") ||
          finalImageUrl.includes("prod-product") ||
          !finalImageUrl.startsWith("http"));

      // Always show fallback for problematic URLs or if no URL
      const shouldUseFallback =
        !finalImageUrl || isProblematicUrl || failedImages.has(record.product_id);

      return (
        <Avatar
          src={shouldUseFallback ? fallbackProductImage : finalImageUrl}
          size={40}
          shape="square"
          style={{
            backgroundColor: shouldUseFallback ? "transparent" : "#f5f5f5",
            border: shouldUseFallback ? "none" : "1px solid #e0e0e0",
          }}
          onError={() => {
            setFailedImages((prev) => new Set([...prev, record.product_id]));
            return false;
          }}
        />
      );
    },
  },
  {
    title: "Product name",
    dataIndex: "product_name",
    key: "product_name",
    render: (text, record) => record.product_name || record.name || "N/A",
  },
  {
    title: "SKU",
    dataIndex: "sku",
    key: "sku",
    render: (text, record) => record.sku || record.product_sku || "N/A",
  },
  {
    title: "Value",
    dataIndex: "value",
    key: "value",
    render: (text, record) => `₹ ${record.value || record.price || 0}`,
  },
  {
    title: "Category",
    dataIndex: "category",
    key: "category",
    render: (text, record) =>
      record.category?.name || record.category || "N/A",
  },
  {
    title: "No. of orders",
    dataIndex: "orders_count",
    key: "orders_count",
    render: (text, record) => record.orders_count || record.orders || 0,
  },
];

  return (
    <div >
      {/* Top Stats Cards - Consolidated */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <CardContainer
            bodyStyle={{ padding: "20px 24px" }}
            bordered={false}
            className="stat-card"
          >
            <div className="consolidated-stats">
              <div className="consolidated-stat-item" onClick={() => handleCardClick("/orders")} style={{ cursor: "pointer" }}>
                <div className="stat-icon-sm">
                  <ShoppingCartOutlined style={{ fontSize: 18, color: "#4F7CFF" }} />
                </div>
                <div className="stat-info">
                  <Text className="stat-label">Total Orders</Text>
                  <Text className="stat-value-compact" style={{ fontSize: getStatFontSize(data.counts ? data.counts.total_orders : 0) }}>
                    {data.counts ? data.counts.total_orders : 0}
                  </Text>
                </div>
              </div>
              <div className="consolidated-stat-divider" />
              <div className="consolidated-stat-item" onClick={() => handleCardClick("/payments")} style={{ cursor: "pointer" }}>
                <div className="stat-icon-sm">
                  <RupeeIcon />
                </div>
                <div className="stat-info">
                  <Text className="stat-label">Revenue</Text>
                  <Text className="stat-value-compact" style={{ fontSize: getStatFontSize(`₹ ${data.counts ? parseFloat(data.counts.total_revenue).toLocaleString("en-IN") : 0}`) }}>
                    ₹{" "}
                    {data.counts
                      ? parseFloat(data.counts.total_revenue).toLocaleString("en-IN")
                      : 0}
                  </Text>
                </div>
              </div>
              <div className="consolidated-stat-divider" />
              <div className="consolidated-stat-item" onClick={() => handleCardClick("/orders")} style={{ cursor: "pointer" }}>
                <div className="stat-icon-sm">
                  <BarChartOutlined style={{ fontSize: 18, color: "#4F7CFF" }} />
                </div>
                <div className="stat-info">
                  <Text className="stat-label">Avg Order Value</Text>
                  <Text className="stat-value-compact" style={{ fontSize: getStatFontSize(`₹ ${data.counts ? parseFloat(data.counts.avg_order_value).toFixed(0) : 0}`) }}>
                    ₹{" "}
                    {data.counts
                      ? parseFloat(data.counts.avg_order_value).toFixed(0)
                      : 0}
                  </Text>
                </div>
              </div>
            </div>
          </CardContainer>
        </Col>
        <Col xs={24} lg={12}>
          <CardContainer
            bodyStyle={{ padding: "20px 24px" }}
            bordered={false}
            className="stat-card"
          >
            <div className="consolidated-stats">
              <div className="consolidated-stat-item" onClick={() => handleCardClick("/users")} style={{ cursor: "pointer" }}>
                <div className="stat-icon-sm">
                  <UserOutlined style={{ fontSize: 18, color: "#4F7CFF" }} />
                </div>
                <div className="stat-info">
                  <Text className="stat-label">Total Customers</Text>
                  <Text className="stat-value-compact" style={{ fontSize: getStatFontSize(data.counts ? data.counts.total_customers : 0) }}>
                    {data.counts ? data.counts.total_customers : 0}
                  </Text>
                </div>
              </div>
              <div className="consolidated-stat-divider" />
              <div className="consolidated-stat-item" onClick={() => handleCardClick("/users")} style={{ cursor: "pointer" }}>
                <div className="stat-icon-sm">
                  <MobileOutlined style={{ fontSize: 18, color: "#4F7CFF" }} />
                </div>
                <div className="stat-info">
                  <Text className="stat-label">App Users</Text>
                  <Text className="stat-value-compact" style={{ fontSize: getStatFontSize(data.counts ? data.counts.app_users : 0) }}>
                    {data.counts ? data.counts.app_users : 0}
                  </Text>
                </div>
              </div>
              <div className="consolidated-stat-divider" />
              <div className="consolidated-stat-item" onClick={() => handleCardClick("/products")} style={{ cursor: "pointer" }}>
                <div className="stat-icon-sm">
                  <ShoppingOutlined style={{ fontSize: 18, color: "#4F7CFF" }} />
                </div>
                <div className="stat-info">
                  <Text className="stat-label">Total Products</Text>
                  <Text className="stat-value-compact" style={{ fontSize: getStatFontSize(data.counts ? data.counts.total_products : 0) }}>
                    {data.counts ? data.counts.total_products : 0}
                  </Text>
                </div>
              </div>
            </div>
          </CardContainer>
        </Col>
      </Row>

      {/* Charts and Tables Row */}
      <Row gutter={[16, 16]}>
        {/* Orders Chart */}
        <Col xs={24} lg={16}>
          <CardContainer
            bordered={false}
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text strong style={{ fontSize: 18 }}>
                  Orders
                </Text>
                <CustomDropdown
                  value={orderChartFilter}
                  onChange={setOrderChartFilter}
                  options={filterOptions}
                  style={{ width: 120 }}
                />
              </div>
            }
            style={{ minHeight: "320px" }}
            bodyStyle={{ padding: 0 }}
          >
            <div style={{ position: "relative", height: "320px" }}>
              {/* Chart or Fallback */}
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Spin size="large" />
                </div>
              ) : data.hourly_chart && data.hourly_chart.length > 0 ? (
                <div style={{ height: "100%", padding: "40px 16px 16px 16px" }}>
                  <Area data={data.hourly_chart} {...areaConfig} />
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    backgroundColor: "#fafafa",
                    color: "#999",
                    fontSize: "14px",
                  }}
                >
                  No chart data available
                </div>
              )}
            </div>
          </CardContainer>
        </Col>

        {/* Order Split Chart */}
        <Col xs={24} lg={8}>
          <CardContainer
            bordered={false}
            title={
              <Text strong style={{ fontSize: 18 }}>
                Order Split
              </Text>
            }
            style={{ minHeight: "320px" }}
            bodyStyle={{ padding: 0 }}
          >
            <div style={{ position: "relative", height: "320px" }}>
              {/* Chart or Fallback */}
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Spin size="large" />
                </div>
              ) : data.piechart && data.piechart.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    padding: "16px",
                  }}
                >
                  {/* Pie Chart with Percentage Labels */}
                  <div style={{ position: "relative", marginBottom: "24px" }}>
                    <Pie data={data.piechart} {...pieConfig} />
                    <div
                      style={{
                        position: "absolute",
                        bottom: "10px",
                        left: "-50px",
                        fontSize: "14px",
                        color: "#999",
                        fontWeight: "500",
                      }}
                    ></div>
                  </div>

                  {/* Legend with counts */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "32px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: "#1f1f1f",
                          borderRadius: "50%",
                        }}
                      ></div>
                      <Text
                        style={{
                          fontSize: "14px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        In-store{" "}
                        <span style={{ fontWeight: "600" }}>
                          {data.piechart.find(
                            (item) => item.type === "in_store"
                          )?.value || 0}
                        </span>
                      </Text>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          backgroundColor: "#4F7CFF",
                          borderRadius: "50%",
                        }}
                      ></div>
                      <Text
                        style={{
                          fontSize: "14px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        Delivery{" "}
                        <span style={{ fontWeight: "600" }}>
                          {data.piechart.find(
                            (item) => item.type === "delivery"
                          )?.value || 0}
                        </span>
                      </Text>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    backgroundColor: "#fafafa",
                    color: "#999",
                    fontSize: "14px",
                  }}
                >
                  No chart data available
                </div>
              )}
            </div>
          </CardContainer>
        </Col>
      </Row>

      {/* Bottom Row */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {/* Recent Activity */}
        <Col xs={24} lg={8}>
          <CardContainer
            bordered={false}
            title={
              <Text strong style={{ fontSize: 18 }}>
                Recent Activity (orders)
              </Text>
            }
            style={{ Height: "320px" }}
          >
            <div
              className="ordrs"
              style={{ maxHeight: "320px", overflowY: "auto" }}
            >
              {loading ? (
                [1, 2, 3, 4, 5, 6].map((a) => (
                  <Skeleton
                    round
                    key={a}
                    active
                    avatar
                    style={{ marginBottom: 16 }}
                  />
                ))
              ) : data.recentOrders && data.recentOrders.length > 0 ? (
                data.recentOrders.slice(0, 6).map((order, index) => (
                  <Row
                    key={index}
                    style={{ marginBottom: 16, alignItems: "center" }}
                  >
                    <Col span={16} style={{ paddingLeft: 0 }}>
                      <Text strong style={{ fontSize: 14 }}>
                        {order.name || order.mobile || "Unknown User"}
                      </Text>
                      <br />
                      <Text style={{ fontSize: 12, color: "#666" }}>
                        {DateFormat(order.created_at)}
                      </Text>
                    </Col>
                    <Col span={8} style={{ textAlign: "right" }}>
                      <Text strong style={{ color: "#52c41a", fontSize: 14 }}>
                        ₹ {order.grand_total ? parseInt(order.grand_total) : 0}
                      </Text>
                    </Col>
                  </Row>
                ))
              ) : (
                <Text style={{ color: "#666" }}>No Recent Orders</Text>
              )}
            </div>
          </CardContainer>
        </Col>

        {/* Top 5 SKUs */}
        <Col xs={24} lg={16}>
          <CardContainer
            bordered={false}
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text strong style={{ fontSize: 18 }}>
                  Top 5 SKUs
                </Text>
                {/* <CustomDropdown
                  value={topSKUsFilter}
                  onChange={setTopSKUsFilter}
                  options={filterOptions}
                  style={{ width: 120 }}
                /> */}
              </div>
            }
            style={{ Height: "520px" }}
          >
            <Table
              columns={topSKUsColumns}
              dataSource={data.topSKUs || []}
              pagination={false}
              size="small"
              style={{ fontSize: 12 }}
              className="clean-table-header"
              loading={loading}
            />
          </CardContainer>
        </Col>
      </Row>
    </div>
  );
}

// const d = (
//   <Row gutter={[12, 12]}>
//     <Col xs={24} sm={12} xl={6}>
//       <CardContainer title="Total Sales">
//         <h2 style={{ color: "green" }}>INR 43256</h2>
//         <h2 style={{ color: "green" }}>+2.5%</h2>
//         <h4 style={{ textAlign: "center", marginTop: 17 }}>Daily Sales</h4>
//       </CardContainer>
//     </Col>
//     <Col xs={24} sm={12} xl={6}>
//       <CardContainer title="Views">
//         <h2>264</h2>
//         <TinyArea {...tinyLineConfig} />
//         <h4 style={{ textAlign: "center" }}>Daily Visits</h4>
//       </CardContainer>
//     </Col>
//     <Col xs={24} sm={12} xl={6}>
//       <CardContainer title="Operational activity effect">
//         <h2>{tinyProgressConfig.percent * 100}%</h2>
//         <Progress style={{ marginTop: 0 }} {...tinyProgressConfig} />
//         <h4 style={{ marginTop: -16, textAlign: "center" }}>
//           12% week-on-year
//         </h4>
//       </CardContainer>
//     </Col>
//     <Col xs={24} sm={12} xl={6}>
//       <CardContainer title="No of Payments">
//         <h2>264</h2>
//         <TinyColumn {...tinyBarConfig} />
//         <h4 style={{ textAlign: "center" }}>Conversion Rate</h4>
//       </CardContainer>
//     </Col>
//     <Col style={{ marginBottom: 5 }} xs={24} lg={12}>
//       <CardContainer
//         tabList={pieTabTitle}
//         activeTabKey={pieTabKey}
//         tabBarExtraContent={<a href="#">More</a>}
//         onTabChange={(key) => setPieTabKey(key)}
//       >
//         {pieTabContent[pieTabKey]}
//       </CardContainer>
//     </Col>
//     <Col style={{ marginBottom: 5 }} xs={24} lg={12}>
//       <CardContainer
//         tabList={barTabTitle}
//         activeTabKey={barTabKey}
//         tabBarExtraContent={<a href="#">More</a>}
//         onTabChange={(key) => setBarTabKey(key)}
//       >
//         {barTabContent[barTabKey]}
//       </CardContainer>
//     </Col>
//   </Row>
// );
